/**
 * PathBuilder — assemble a `THREE.CurvePath<Vector3>` for a Part.
 *
 * One CurvePath per part, chaining all module curves end-to-end.
 * Per module:
 *   - Straight → THREE.LineCurve3 (module geometry baked in)
 *   - Curve    → XtsArcCurve3 (analytic)
 *   - Free     → XtsPointCloudCurve3
 *
 * Each subcurve here is expressed in **module-local coordinates** (origin at
 * module entry). The world transform of the module = chain.startWorldPose
 * (planar) is applied at render time by the `<XtsModule>` group node.
 *
 * Helper `chainPoseToMatrix4` converts a planar pose to a Three.js Matrix4
 * (Z-up world).
 */

import {
  CurvePath,
  LineCurve3,
  Matrix4,
  Quaternion,
  Vector3,
} from 'three';
import { XtsArcCurve3 } from './XtsArcCurve3.js';
import { XtsPointCloudCurve3 } from './XtsPointCloudCurve3.js';
import {
  buildChain,
  type BuiltChain,
  type BuiltModule,
  type PlanarPose,
} from './ChainBuilder.js';
import type { ModuleEntry } from '../types.js';

export interface PartPath {
  chain: BuiltChain;
  /** A CurvePath chaining all modules (in module-local frames per subcurve). */
  curvePath: CurvePath<Vector3>;
}

/**
 * Build a Part path. Returns the BuiltChain plus a CurvePath for visual
 * debug splines (continuity overlay). The CurvePath subcurves live in
 * MODULE-LOCAL coordinates; the consumer is responsible for placing each
 * module under a parent group with `startWorldPose` applied.
 */
export function buildPartPath(
  modules: ReadonlyArray<ModuleEntry>,
): PartPath {
  const chain = buildChain(modules);
  const path = new CurvePath<Vector3>();
  for (const built of chain.modules) {
    pushModuleCurves(path, built);
  }
  // Auto-update segment lengths so getPointAt is correctly arc-length-weighted.
  path.updateArcLengths();
  return { chain, curvePath: path };
}

function pushModuleCurves(
  path: CurvePath<Vector3>,
  built: BuiltModule,
): void {
  switch (built.catalog.pathType) {
    case 'Straight': {
      path.add(
        new LineCurve3(
          new Vector3(0, 0, 0),
          new Vector3(built.catalog.moduleLengthMm, 0, 0),
        ),
      );
      break;
    }
    case 'Curve': {
      path.add(new XtsArcCurve3(built.catalog));
      break;
    }
    case 'Free': {
      path.add(new XtsPointCloudCurve3(built.catalog));
      break;
    }
  }
}

/**
 * Convert a planar pose (X,Y,yaw around +Z) to a Three.js Matrix4
 * (Z-up world).
 */
export function poseToMatrix4(
  pose: PlanarPose,
  out = new Matrix4(),
): Matrix4 {
  const q = new Quaternion().setFromAxisAngle(
    new Vector3(0, 0, 1),
    (pose.yawDeg * Math.PI) / 180,
  );
  out.compose(
    new Vector3(pose.positionMm[0], pose.positionMm[1], pose.positionMm[2]),
    q,
    new Vector3(1, 1, 1),
  );
  return out;
}
